import type { MutableRefObject, ReactNode } from 'react'
import { Controller, type Control, type UseFormClearErrors } from 'react-hook-form'
import Checkbox from '@mui/joy/Checkbox'
import Box from '@mui/joy/Box'
import Typography from '@mui/joy/Typography'
import { Trans } from 'react-i18next'

import type { CheckoutFormValues } from '@/components/checkout/page/controller'
import type { CheckoutDisclaimerLinks } from '@/types/reservation-checkout'

const CHECKOUT_DISCLAIMER_CHECKBOX_ID = 'checkout-disclaimer-accepted'
const CHECKOUT_DISCLAIMER_TEXT_ID = 'checkout-disclaimer-text'
const CHECKOUT_DISCLAIMER_ERROR_ID = 'checkout-disclaimer-error'

function DisclaimerLink({
    href,
    children,
}: {
    href: string | null
    children?: ReactNode
}) {
    if (!href) {
        return (
            <span style={{ textDecoration: 'underline' }}>
                {children}
            </span>
        )
    }

    return (
        <a href={href} target="_blank" rel="nofollow noreferrer">
            {children}
        </a>
    )
}

export default function CheckoutDisclaimer({
    links,
    control,
    clearErrors,
    sectionRef,
} : {
    links: CheckoutDisclaimerLinks
    control: Control<CheckoutFormValues>
    clearErrors: UseFormClearErrors<CheckoutFormValues>
    sectionRef?: MutableRefObject<HTMLDivElement | null>
}) {
    return (
        <Box
            ref={sectionRef}
            sx={{
                minWidth: 0,
                scrollMarginBlock: 'calc(70px + 1rem)',
            }}
        >
            <Controller
                name="checkout_disclaimer_accepted"
                control={control}
                render={({ field, fieldState }) => {
                    const errorMessage = typeof fieldState.error?.message === 'string'
                        ? fieldState.error.message
                        : null

                    const updateChecked = (checked: boolean) => {
                        field.onChange(checked)

                        if (checked) {
                            clearErrors('checkout_disclaimer_accepted')
                        }
                    }

                    return (
                        <>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 1.5,
                                    pl: 1,
                                }}
                            >
                                <Checkbox
                                    id={CHECKOUT_DISCLAIMER_CHECKBOX_ID}
                                    name={field.name}
                                    checked={field.value}
                                    onBlur={field.onBlur}
                                    onChange={event => updateChecked(event.target.checked)}
                                    color={errorMessage ? 'danger' : 'neutral'}
                                    size="lg"
                                    slotProps={{
                                        input: {
                                            'aria-labelledby': CHECKOUT_DISCLAIMER_TEXT_ID,
                                            'aria-describedby': errorMessage ? CHECKOUT_DISCLAIMER_ERROR_ID : undefined,
                                            'aria-invalid': Boolean(errorMessage),
                                        },
                                    }}
                                    sx={{
                                        m: 0,
                                        '& .MuiCheckbox-checkbox': {
                                            mt: '2px',
                                        },
                                    }}
                                />
                                <Box sx={{ flex: 1, minWidth: 0, pt: '1px' }}>
                                    <Typography
                                        id={CHECKOUT_DISCLAIMER_TEXT_ID}
                                        component="span"
                                        level="body-xs"
                                        style={{ textAlign: 'left' }}
                                        sx={{
                                            display: 'block',
                                            textAlign: 'left',
                                            '& a': {
                                                color: 'inherit',
                                                textDecoration: 'underline',
                                            },
                                            '& label': {
                                                display: 'inline',
                                                cursor: 'pointer',
                                            },
                                        }}
                                    >
                                        <Trans
                                            i18nKey="checkout.termsOfServiceHtml"
                                            components={{
                                                checkboxLabel: <label htmlFor={CHECKOUT_DISCLAIMER_CHECKBOX_ID} />,
                                                termsLink: <DisclaimerLink href={links.terms_url} />,
                                                privacyPolicyLink: <DisclaimerLink href={links.privacy_policy_url} />,
                                                garageTermsLink: <DisclaimerLink href={links.garage_terms_url} />,
                                            }}
                                        />
                                    </Typography>
                                </Box>
                            </Box>
                            {errorMessage ? (
                                <Typography
                                    id={CHECKOUT_DISCLAIMER_ERROR_ID}
                                    level="body-xs"
                                    color="danger"
                                    sx={{ mt: 0.5, pl: '44px' }}
                                >
                                    {errorMessage}
                                </Typography>
                            ) : null}
                        </>
                    )
                }}
            />
        </Box>
    )
}
